import type { InventoryListItem } from '@/lib/api/schemas/inventory.schema'
import type { YmEconomicsBulkRow } from '@/lib/api/schemas/ymarket-pricing.schema'
import { formatRubleAmount } from '@/lib/market-money'

function rubles(value: number | null): string {
  return value === null ? '—' : formatRubleAmount(value)
}

function ceil100(value: number): number {
  return Math.ceil(value / 100 - Number.EPSILON) * 100
}

function changePercent(current: number | null, next: number | null): number | null {
  if (current === null || current <= 0 || next === null) return null
  return (next / current - 1) * 100
}

function percent(value: number | null): string {
  if (value === null) return '—'
  return `${value > 0 ? '+' : ''}${value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} %`
}

function refusalText(row: YmEconomicsBulkRow): string {
  return row.refusals.map((refusal) => refusal.message).join('; ')
}

function nameOf(row: YmEconomicsBulkRow, item: InventoryListItem | undefined): string {
  return item?.title?.trim() || row.label.split(' · ')[0] || row.label
}

function articleOf(row: YmEconomicsBulkRow, item: InventoryListItem | undefined): string | null {
  return item?.article ?? row.label.split(' · ')[1] ?? null
}

function sizeOf(row: YmEconomicsBulkRow, item: InventoryListItem | undefined): string | null {
  if (item?.techSize) return `р. ${item.techSize}`
  const size = row.label.split(' · ')[2] ?? null
  return size === null || size.startsWith('р.') ? size : `р. ${size}`
}

function AutoDetails({ row }: { row: YmEconomicsBulkRow }) {
  const auto = row.auto
  if (auto === null) return null
  const estimate = row.showcaseEstimate
  return (
    <details className="relative shrink-0" data-testid={`reprice-auto-details-${row.productId}`}>
      <summary
        aria-label={`Сведения о расчёте: ${row.label}`}
        className="flex h-7 w-7 cursor-pointer list-none items-center justify-center rounded-full border border-rule text-xs font-semibold text-ink-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        title="Сведения о расчёте"
      >i</summary>
      <dl className="absolute right-0 z-10 mt-1 grid w-[min(19rem,calc(100vw-3rem))] gap-1 rounded-lg border border-rule bg-white p-3 text-xs shadow-lg">
        <div><dt className="font-semibold">Прогноз прибыли</dt><dd>{rubles(auto.projectedProfit)}</dd></div>
        <div><dt className="font-semibold">Витрина с Pay / без Pay</dt><dd>{rubles(auto.showcasePay)} / {rubles(auto.showcaseNonPay)}</dd></div>
        {estimate === null ? null : (
          <div><dt className="font-semibold">Основание оценки витрины</dt><dd>{estimate.basis === 'own' ? 'свои данные' : 'похожие изделия'} · {estimate.reportCollectedAt}</dd></div>
        )}
        <div><dt className="font-semibold">Наценка к Cg / себестоимости 1С</dt><dd>{percent(auto.markupVsCgPercent)} / {percent(auto.markupVsOnecPercent)}</dd></div>
        <div><dt className="font-semibold">Надбавка на камни · резерв</dt><dd>{rubles(auto.stoneAllowance)} · {rubles(auto.stoneReserve)}</dd></div>
        <div><dt className="font-semibold">Основание цены</dt><dd>{auto.bindingReason ?? '—'}</dd></div>
        <div><dt className="font-semibold">Себестоимость 1С</dt><dd>{auto.inputs.onecCostDate ?? 'дата неизвестна'} · {rubles(auto.inputs.onecCost)}</dd></div>
        <div><dt className="font-semibold">Курс ЦБ</dt><dd>{auto.inputs.cbDate ?? 'дата неизвестна'} · {auto.inputs.cbRate?.toLocaleString('ru-RU', { maximumFractionDigits: 4 }) ?? '—'}</dd></div>
        <div><dt className="font-semibold">Тарифы Маркета</dt><dd>{auto.inputs.feeTariffSnapshotAt ?? 'снимок неизвестен'} · {auto.inputs.feeRate === null ? '—' : `${(auto.inputs.feeRate * 100).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} %`}</dd></div>
        <div><dt className="font-semibold">Состав металла и вставок</dt><dd>{auto.inputs.compositionConventionVersion}</dd></div>
        {auto.promos.length === 0 ? null : (
          <div>
            <dt className="font-semibold">Акции Маркета</dt>
            <dd className="space-y-2">
              {auto.promos.map((promo) => (
                <div key={promo.promoId}>
                  <p className="font-medium">{promo.name} · {promo.participating ? 'участвует' : 'не участвует'}</p>
                  <p>Цена продавца: {rubles(promo.sellerPromoPrice)} · покупателя: {rubles(promo.buyerPrice)}</p>
                  <p>После доставки и налога: {rubles(promo.netAfterDeliveryTax)} · минимум маржи: {rubles(promo.marginFloorAmount)} · {promo.keepsMarginFloor === null ? 'маржа не рассчитана' : promo.keepsMarginFloor ? 'выше минимума' : 'ниже минимума'}</p>
                  {promo.unavailableReason === null ? null : <p>{promo.unavailableReason}</p>}
                </div>
              ))}
            </dd>
          </div>
        )}
        {row.warnings.map((warning, index) => (
          <div key={`${warning.code}:${index}`}><dt className="font-semibold">Замечание</dt><dd>{warning.message}</dd></div>
        ))}
      </dl>
    </details>
  )
}

export function AutoRepriceList({
  rows,
  refusedRows,
  inventoryRows,
  prices,
  disabled,
  onPriceChange,
}: {
  rows: readonly YmEconomicsBulkRow[]
  refusedRows: readonly YmEconomicsBulkRow[]
  inventoryRows: readonly InventoryListItem[]
  prices: Readonly<Record<string, string>>
  disabled: boolean
  onPriceChange: (productId: string, value: string) => void
}) {
  const inventoryById = new Map(inventoryRows.map((row) => [row.productId, row]))
  return (
    <div className="space-y-2" data-testid="reprice-auto-rows">
      {rows.map((row) => {
        const auto = row.auto
        if (auto === null) return null
        const item = inventoryById.get(row.productId)
        const name = nameOf(row, item)
        const rawPrice = prices[row.productId] ?? String(row.newPrice ?? '')
        const editedPrice = rawPrice.trim() === '' ? null : Number(rawPrice)
        const validPrice = editedPrice !== null && Number.isInteger(editedPrice) && editedPrice > 0
        const belowFloor = validPrice && auto.costFloor !== null && editedPrice < auto.costFloor
        const crossed = validPrice && auto.crossedFactor !== null
          ? ceil100(editedPrice * auto.crossedFactor)
          : row.newOldPrice
        const minimum = validPrice && auto.costBoundary !== null && auto.promoFactor !== null
          ? ceil100(Math.max(auto.costBoundary, editedPrice * auto.promoFactor))
          : row.newMinimumForPromos
        const movement = changePercent(row.currentPrice, validPrice ? editedPrice : null)
        const overThreshold = movement !== null
          && auto.confirmationThresholdPercent !== null
          && Math.abs(movement) > auto.confirmationThresholdPercent
        return (
          <article
            key={row.productId}
            className={`grid min-w-0 grid-cols-[3rem_minmax(0,1fr)] gap-2 rounded-lg border p-2 text-sm sm:grid-cols-[3.5rem_minmax(0,1.3fr)_minmax(5rem,.7fr)_minmax(5.5rem,.8fr)_minmax(6rem,.9fr)_2rem] sm:items-center ${overThreshold ? 'border-gold-deep/50 bg-bone/70' : 'border-rule'}`}
            data-testid={`reprice-auto-row-${row.productId}`}
            data-over-threshold={overThreshold ? 'true' : 'false'}
          >
            {item?.primaryMediaUrl ? (
              <img className="row-span-2 h-12 w-12 rounded-md object-cover sm:row-span-1 sm:h-14 sm:w-14" src={item.primaryMediaUrl} alt="" />
            ) : (
              <div aria-hidden="true" className="row-span-2 h-12 w-12 rounded-md bg-bone sm:row-span-1 sm:h-14 sm:w-14" />
            )}
            <div className="min-w-0 self-center">
              <h3 className="break-words font-semibold">{name}</h3>
              <p className="break-words text-xs text-ink-muted">
                {[articleOf(row, item), sizeOf(row, item)]
                  .filter((part): part is string => part !== null && part.trim() !== '')
                  .join(' · ') || 'Артикул и размер не указаны'}
              </p>
            </div>
            <p className="tabular-nums text-xs sm:text-sm">
              <span className="block text-xs text-ink-muted">Вес металла</span>
              {row.weightGrams === null ? '—' : `${row.weightGrams.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} г`}
            </p>
            <p className="tabular-nums text-xs sm:text-sm">
              <span className="block text-xs text-ink-muted">Сейчас</span>
              <span className="block">{rubles(row.currentPrice)}</span>
              {row.currentOldPrice === null ? null : <span className="block text-xs text-ink-muted line-through">{rubles(row.currentOldPrice)}</span>}
            </p>
            <label className="min-w-0 tabular-nums">
              <span className="block text-xs text-ink-muted">Станет · {percent(movement)}</span>
              <input
                className="input mt-1 w-full min-w-0 px-2 py-1 text-base"
                type="number"
                inputMode="numeric"
                min="1"
                step="1"
                value={rawPrice}
                disabled={disabled}
                aria-label={`Станет, ${name}`}
                onChange={(event) => onPriceChange(row.productId, event.target.value)}
              />
              <span className="mt-1 block text-[11px] text-ink-muted">Зачёркнутая: {rubles(crossed)}</span>
              <span className="block text-[11px] text-ink-muted">Мин. для акций: {rubles(minimum)}</span>
              {belowFloor ? <span className="mt-1 block text-xs text-st-unpaid">Ниже себестоимости: минимум {rubles(auto.costFloor)}</span> : null}
              {!validPrice ? <span className="mt-1 block text-xs text-st-unpaid">Введите целую цену в рублях.</span> : null}
            </label>
            <div className="col-start-2 flex items-start justify-between gap-2 sm:col-start-auto sm:justify-end">
              <AutoDetails row={row} />
            </div>
          </article>
        )
      })}
      {refusedRows.length === 0 ? null : (
        <details className="rounded-lg border border-rule px-3 py-2 text-sm" data-testid="reprice-auto-refusals">
          <summary className="cursor-pointer select-none font-medium">Не переоцениваются: {refusedRows.length}</summary>
          <ul className="mt-2 space-y-2 text-ink-2">
            {refusedRows.map((row) => (
              <li key={row.productId} data-testid="reprice-refusal">
                <span className="font-medium">{nameOf(row, inventoryById.get(row.productId))}</span>
                <span> — {refusalText(row)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}
